import React, { useContext, useEffect } from "react";
import PropTypes from "prop-types";
import qapi from "views_shared/app/javascript/api";
import { mediaProfileUrl } from "views_shared/app/javascript/utils/urls";
import ProfilePage from "../profiles/ProfilePage";
import ViewersAlsoView, { useViewersAlsoView } from "../profiles/ViewersAlsoView";
import ConnectSection, { connectTitle, firstName } from "../profiles/sections/ConnectSection";
import { MediaProfileContext } from "./data/media_profile_context_management";
import useMediaOutletsRail from "./data/use_media_outlets_rail";
import MediaPersonHeader from "./show/media_person_header";
import MediaSections from "./show/media_sections";
import MediaOutletsRailCard from "./show/media_outlets_rail_card";
import MediaProfileSkeleton from "./show/media_profile_skeleton";
import MediaProfileEditModal from "./edit/edit_modal";

// Hydrate adapter for the Viewers-also-view rail: loads display rows for the similar-profile ids
// through the media_profiles view, whose RLS drops any profile this viewer can't see.
const hydrateSimilarMediaProfiles = async (ids) => {
  const response = await qapi.mediaProfiles.getAllById(ids);
  const rows = Array.isArray(response.data) ? response.data : [];
  return rows.map((row) => ({
    // The hook matches hydrated rows back to the similarity results by `id`, so the profile id
    // must go under that name here. The view is keyed on profile_id (see api/media_profiles.js).
    id: row.profile_id,
    href: mediaProfileUrl({ slug: row.slug, migrate_id: row.migrate_id }),
    imageUrl: row.profile_image_url,
    title: row.name,
    subtitle: row.title_at_workplace,
  }));
};

// Media Profile show (the inner component under MediaProfileTopLevelShow's provider): the shared
// <ProfilePage> chassis with a <PersonHeader> + section cards, plus a right rail carrying the shared
// Viewers-also-view similarity card. The designed Covers/clips rail content has no V2 data yet.
function MediaProfile({ mediaProfileId }) {
  const {
    setMediaProfileId,
    isLoading,
    asyncError,
    mediaProfile,
    mediaJobs,
    contactInfos,
    viewerContext,
    setOpenEditModal,
  } = useContext(MediaProfileContext);

  // The similarity query needs the numeric profile id (the route param may be a slug), so it idles
  // until the profile row has loaded — which the page-level skeleton covers anyway.
  const viewersAlsoView = useViewersAlsoView({
    queryModelType: "Id::Profile::MediaProfile",
    queryModelId: mediaProfile?.profile_id,
    hydrate: hydrateSimilarMediaProfiles,
  });

  // Same idle-until-loaded gate as viewersAlsoView above: the RPC needs the numeric profile id,
  // which the page-level skeleton already covers while it's unknown. The base outlet list itself
  // comes from mediaJobs (already fetched for the Employment section), not from this RPC.
  const mediaOutletsRail = useMediaOutletsRail({ mediaProfileId: mediaProfile?.profile_id, mediaJobs });

  // The Connect card (Message lives in the header). A viewer who may edit this page (the profile's
  // own user, or an admin) gets the raw contact_infos rows — including email and phone, which the
  // aggregate below leaves out for everyone — and an edit pen; every other viewer keeps the
  // read-only aggregate.
  const editable = !!viewerContext?.editable;
  const contactDetails = editable ? contactInfos : mediaProfile?.contact_details || [];
  // An owner with no contact rows yet still gets the card, so the edit pen (and the shared
  // "No contact details added yet." empty copy) is always reachable.
  const hasConnectContent = editable || contactDetails.length > 0;

  useEffect(() => {
    setMediaProfileId(mediaProfileId);
  }, [mediaProfileId, setMediaProfileId]);

  // Let the top-level ErrorBoundary handle async failures.
  if (asyncError) throw asyncError;

  // Rails found the profile but the API returned no row — the viewer isn't entitled to it (the
  // PostgREST view enforces visibility/RLS; the Rails route doesn't). Say so instead of
  // skeleton-ing forever.
  if (!isLoading && !mediaProfile) {
    return <div className="alert alert-secondary">This profile isn&apos;t available.</div>;
  }

  // ProfilePage sizes its columns on `rightRail` truthiness alone, so a fragment of self-collapsing
  // cards would leave a phantom rail: the page has to know both cards' states. The rail stays while
  // either has items or is still loading, and goes away only once both settle empty — then the main
  // column takes the full width. The one-time reflow that causes is confined to that case; the common
  // case renders rail skeletons, then items, with no layout snap.
  const railHasContent =
    mediaOutletsRail.isLoading ||
    mediaOutletsRail.outlets.length > 0 ||
    hasConnectContent ||
    viewersAlsoView.isLoading ||
    viewersAlsoView.items.length > 0;

  return (
    <div>
      <MediaProfileEditModal />

      <ProfilePage
        isLoading={isLoading}
        skeleton={<MediaProfileSkeleton />}
        header={<MediaPersonHeader />}
        rightRail={
          railHasContent ? (
            <>
              <MediaOutletsRailCard outlets={mediaOutletsRail.outlets} isLoading={mediaOutletsRail.isLoading} />
              {hasConnectContent && (
                <ConnectSection
                  id="media-connect"
                  title={connectTitle(mediaProfile?.first_name || firstName(mediaProfile?.name))}
                  contactDetails={contactDetails}
                  editable={editable}
                  onEdit={() => setOpenEditModal("CONTACT")}
                  editButtonId="modal-button-CONTACT"
                />
              )}
              <ViewersAlsoView items={viewersAlsoView.items} isLoading={viewersAlsoView.isLoading} />
            </>
          ) : null
        }
      >
        <MediaSections />
      </ProfilePage>
    </div>
  );
}

MediaProfile.propTypes = {
  mediaProfileId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default MediaProfile;
